import { ConversationResource } from '@/types';
import React, { useRef, useMemo, useEffect } from 'react';
import { observer, useComputed } from '@legendapp/state/react';
import { adviceState, adviceSelectors } from '@/lib/store/advice.store';
import { Message } from '@/components/features/chat/components/messages/Message';

/**
 * Renders conversation messages with proper loading states and animations.
 * Prevents duplicate message rendering by using memo and proper key management.
 */
export const ConversationMessages = observer(() => {
  // Computed values from store
  const storeState = useComputed(() => {
    return {
      conversation: adviceState.activeConversation.get(),
      storeAdvices: adviceState.advices.get(),
      status: adviceState.status.get(),
      isStreaming: adviceSelectors.isStreaming(),
      isChatLoading: adviceState.isChatLoading.get(),
      streamingAdviceId: adviceState.streamingAdviceId.get(),
      isTransitioning: adviceState.isTransitioning.get()
    };
  });
  
  // Extract values from computed state to ensure reactivity
  const { 
    conversation, 
    storeAdvices, 
    status, 
    isStreaming, 
    isChatLoading, 
    streamingAdviceId,
    isTransitioning 
  } = storeState.get();

  const bottomRef = useRef<HTMLDivElement>(null);

  /**
   * Deduplicates advices based on ID and question content.
   * Prioritizes store advices over conversation advices and handles streaming state.
   */
  const dedupedAdvices = useMemo(() => {
    const sourceAdvices = storeAdvices.length > 0 
      ? storeAdvices 
      : (conversation?.advices || []);
    
    const seenQuestions = new Set();
    const processedIds = new Set();
    
    const result = [];
    for (let i = sourceAdvices.length - 1; i >= 0; i--) {
      const advice = sourceAdvices[i];
      if (!advice || !advice.id) continue;
      
      if (processedIds.has(advice.id)) continue;
      processedIds.add(advice.id);
      
      if (advice.question) {
        const questionKey = `${advice.question}`.trim().toLowerCase();
        if (seenQuestions.has(questionKey)) {
          if (advice.id !== streamingAdviceId) {
            continue;
          }
        }
        seenQuestions.add(questionKey);
      }
      
      result.unshift(advice);
    }
    
    return result;
  }, [storeAdvices, conversation, streamingAdviceId]);

  /**
   * Scrolls to bottom of conversation when messages change
   */
  useEffect(() => {
    if(!isStreaming) {
      const timer = setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [dedupedAdvices, status, isStreaming]);

  return (
    <>
      <div
        key={`conversation-${conversation?.id || 'empty'}`}
        className='space-y-4'
        data-testid='chat-content'
        data-store-status={status}
        data-conversation-id={conversation?.id}
        data-transitioning={isTransitioning}
        data-advice-count={dedupedAdvices.length}
        data-is-streaming={isStreaming ? 'true' : 'false'}
        data-is-loading={isChatLoading ? 'true' : 'false'}
      >
        {dedupedAdvices.map((advice, idx) => {
          if (!advice) {
            return null;
          }
          
          const messageId = `message-${advice.id}-idx-${idx}-conv-${conversation?.id || 'temp'}`;
          const isLast = idx === dedupedAdvices.length - 1;

          return (
            <Message
              key={messageId}
              advice={advice}
              isLast={isLast}
            />
          );
        })}
      </div>
      <div ref={bottomRef} className='h-px w-full' />
    </>
  );
});
